import React, { useRef, useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import * as MdIcons from 'react-icons/md';

const FiX = FiIcons.FiX as unknown as React.FC;
const FiRefreshCw = FiIcons.FiRefreshCw as unknown as React.FC;
const MdCameraAlt = MdIcons.MdCameraAlt as unknown as React.FC;

interface SelfieCaptureProps {
  onCapture: (file: File) => void;
  onCancel: () => void;
  existingImage?: string;
}

const SelfieCapture: React.FC<SelfieCaptureProps> = ({ onCapture, onCancel, existingImage }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(existingImage || null);
  const [error, setError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  useEffect(() => {
    if (!existingImage && !capturedImage) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = async () => {
    try {
      setError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user', // Front-facing camera for selfie
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err: any) {
      setError('Unable to access camera. Please allow camera permissions.');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      setIsCapturing(false);
      return;
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob and create File
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const imageUrl = URL.createObjectURL(blob);
        setCapturedImage(imageUrl);
        stopCamera();
        onCapture(file);
        setIsCapturing(false);
      } else {
        setIsCapturing(false);
        setError('Failed to capture image');
      }
    }, 'image/jpeg', 0.9);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    if (existingImage) {
      setCapturedImage(null);
    }
    startCamera();
  };

  const handleCancel = () => {
    stopCamera();
    onCancel();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <button
        onClick={handleCancel}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'rgba(255, 255, 255, 0.2)',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          color: 'white',
          fontSize: '24px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <FiX />
      </button>

      {error && (
        <div style={{
          color: '#ff4444',
          backgroundColor: 'rgba(255, 68, 68, 0.1)',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          {error}
          <div style={{ marginTop: '10px' }}>
            <button
              onClick={startCamera}
              style={{
                padding: '8px 16px',
                background: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {capturedImage ? (
        <div style={{ textAlign: 'center' }}>
          <img
            src={capturedImage}
            alt="Captured selfie"
            style={{
              maxWidth: '100%',
              maxHeight: '60vh',
              borderRadius: '8px',
              border: '3px solid white'
            }}
          />
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={retakePhoto}
              style={{
                padding: '12px 24px',
                background: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FiRefreshCw /> Retake
            </button>
            <button
              onClick={handleCancel}
              style={{
                padding: '12px 24px',
                background: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Use This Photo
            </button>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', width: '100%', maxWidth: '640px' }}>
          <div style={{ marginBottom: '20px', color: 'white' }}>
            <h2 style={{ margin: '0 0 10px 0' }}>Take a Selfie</h2>
            <p style={{ margin: 0, opacity: 0.8 }}>Position your face in the frame</p>
          </div>
          
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '640px',
            aspectRatio: '4/3',
            backgroundColor: '#000',
            borderRadius: '8px',
            overflow: 'hidden',
            margin: '0 auto 20px'
          }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)' // Mirror effect for selfie
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>

          <button
            onClick={capturePhoto}
            disabled={!stream || isCapturing}
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              border: '4px solid white',
              background: isCapturing ? '#666' : '#4caf50',
              cursor: isCapturing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '32px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}
          >
            {isCapturing ? <FiRefreshCw /> : <MdCameraAlt />}
          </button>
        </div>
      )}
    </div>
  );
};

export default SelfieCapture;
